import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertWaitlistSchema, type InsertWaitlistEntry } from "@shared/schema";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { captureException, setUserContext } from "@/lib/sentry";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { ArrowRight } from "lucide-react";
import WaitlistConfirmationOverlay from "./waitlist-confirmation-overlay";
import { useTranslations } from "@/hooks/use-translations";
import { useLocale } from "@/components/locale-provider";
import { Link } from "wouter";

interface WaitlistFormProps {
  onPlayVideo?: () => void;
}

export function WaitlistForm({ onPlayVideo }: WaitlistFormProps) {
  const { toast } = useToast();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const { locale } = useLocale();
  const { t } = useTranslations(locale);

  const form = useForm<InsertWaitlistEntry>({
    resolver: zodResolver(insertWaitlistSchema),
    defaultValues: {
      email: "",
      opt_in: true,
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: async (data: InsertWaitlistEntry) => {
      console.log('Submitting form data:', data);
      try {
        // Set user context in Sentry when form is submitted
        setUserContext(data.email);
        
        const res = await apiRequest("POST", "/api/waitlist", data);
        console.log('API response:', res.status);
        return res.json();
      } catch (error) {
        console.error('Form submission error:', error);
        throw error;
      }
    },
    onSuccess: (data, variables) => {
      setSubmittedEmail(variables.email);
      setShowConfirmation(true);
      form.reset();
    },
    onError: (error: unknown) => {
      console.error('Detailed error in mutation:', error);
      
      // Capture the error in Sentry with context
      captureException(error, { 
        form: form.getValues(),
        component: 'WaitlistForm',
        action: 'form_submission' 
      });
      
      // Handle different error types
      let errorMessage = 'Unknown error occurred';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null && 'message' in error) {
        errorMessage = (error as { message: string }).message;
      }
      toast({
        variant: "destructive",
        title: "Error Submitting Form",
        description: errorMessage,
      });
    },
  });

  const handlePlayVideo = () => {
    if (onPlayVideo) {
      setShowConfirmation(false);
      onPlayVideo();
    }
  };

  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((data) => mutate(data))}
          className="flex flex-col space-y-4 w-full"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="flex items-center">
                    <Input
                      placeholder={t("home").waitlist.emailPlaceholder}
                      type="email"
                      className="bg-transparent border-0 text-[#fffcf5] placeholder:text-[#fffcf5] text-sm tracking-[0.28px] h-full focus:ring-0"
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="space-y-1 leading-none mt-2">
            <p className="text-sm font-medium leading-none text-[#fffcf5]">
              {t("home").waitlist.optInText}{" "}
              <Link href="/waiting-list/privacy-notice">
                <span className="underline cursor-pointer inline-block">
                  {t("home").waitlist.privacyNoticeLink}
                </span>
              </Link>
            </p>
          </div>

          <Button
            type="submit"
            disabled={isPending}
            className="bg-[#dd2c67] hover:bg-[#dd2c67]/90 h-[63px] text-lg tracking-[0.54px] text-[#f4eadf] uppercase flex items-center justify-center space-x-2"
          >
            <span>{t("home").waitlist.submitButton}</span>
            <ArrowRight className="h-6 w-6" />
          </Button>
        </form>
      </Form>

      {showConfirmation && (
        <WaitlistConfirmationOverlay
          email={submittedEmail}
          onClose={() => setShowConfirmation(false)}
          onPlayVideo={handlePlayVideo}
        />
      )}
    </>
  );
}
